import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { motion, AnimatePresence } from 'framer-motion';
import { GET_CATEGORIES } from '../graphql/queries';
import {
  ChevronRightIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisH } from '@fortawesome/free-solid-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';

library.add(fas);

interface Category {
  id: string;
  name: string;
  slug: string;
  icon: string;
  image?: string;
  hasChildren: boolean;
  childrenList?: Category[];
}

const CategorySidebar: React.FC = () => {
  const location = useLocation();
  const { data, loading } = useQuery(GET_CATEGORIES);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const categories: Category[] = data?.rootCategories || [];
  const currentCategory = location.pathname.startsWith('/category/')
    ? location.pathname.split('/')[2]
    : null;

  const parseIconClass = (iconClass: string) => {
    if (!iconClass) return null;
    const parts = iconClass.trim().split(' ');
    if (parts.length >= 2) {
      const prefix = parts[0];
      const iconName = parts[1].replace(/-([a-z])/g, (g) => g[1].toUpperCase());
      return { prefix, iconName };
    }
    return null;
  };



  return (
    <div style={{ paddingRight: 8, paddingBottom: 16 }}>
      {/* Main Categories Card */}
      <div
        style={{
          background: 'var(--pw-surface)',
          border: '1px solid var(--pw-border)',
          borderRadius: 0,
          boxShadow: '0 4px 18px rgba(116, 67, 35, 0.05)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          className="d-flex align-items-center gap-2"
          style={{
            padding: '18px 20px',
            borderBottom: '1px solid var(--pw-border-soft)',
            background: 'var(--pw-surface-tint)',
          }}
        >
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-strong) 100%)',
              color: 'white',
            }}
          >
            <Squares2X2Icon style={{ width: 20, height: 20 }} />
          </div>
          <h2 className="fw-bold mb-0" style={{ fontSize: 16, color: 'var(--pw-text)' }}>
            Categories
          </h2>
        </div>

        {/* Loading */}
        {loading && (
          <div className="p-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="d-flex align-items-center gap-3 mb-3">
                <div className="skeleton" style={{ width: 36, height: 36, borderRadius: 10 }} />
                <div className="skeleton" style={{ height: 14, width: '60%', borderRadius: 6 }} />
              </div>
            ))}
          </div>
        )}

        {/* Category List */}
        <nav style={{ padding: '12px 8px' }}>
          {categories.map((category) => {
            const isExpanded = expandedId === category.id;
            const isHovered = hoveredId === category.id;
            const isActive = currentCategory === category.slug;
            const hasChildren = category.hasChildren && (category.childrenList?.length || 0) > 0;

            return (
              <div
                key={category.id}
                onMouseEnter={() => {
                  setHoveredId(category.id);
                  if (hasChildren) setExpandedId(category.id);
                }}
                onMouseLeave={() => {
                  setHoveredId(null);
                  if (hasChildren) setExpandedId(null);
                }}
              >
                <Link
                  to={`/category/${category.slug}`}
                  className="d-flex align-items-center gap-3 text-decoration-none"
                  style={{
                    padding: '10px 14px',
                    borderRadius: 14,
                    background: isActive
                      ? 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-strong) 100%)'
                      : isHovered
                        ? 'var(--pw-bg-soft)'
                        : 'transparent',
                    color: isActive ? 'white' : 'var(--pw-text)',
                    transition: 'all 0.15s ease',
                    marginBottom: 2,
                  }}
                >
                  <span
                    className="d-flex align-items-center justify-content-center"
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      background: isActive
                        ? 'rgba(255,255,255,0.2)'
                        : 'var(--pw-surface-tint)',
                      fontSize: 18,
                      flexShrink: 0,
                    }}
                  >
                    {(() => {
                      const parsed = parseIconClass(category.icon);
                      return parsed ? (
                        <FontAwesomeIcon icon={parsed as any} style={{ fontSize: 16 }} />
                      ) : (
                        <FontAwesomeIcon icon={faEllipsisH} style={{ fontSize: 16 }} />
                      );
                    })()}
                  </span>
                  <span
                    className="fw-medium flex-grow-1"
                    style={{
                      fontSize: 14,
                      color: isActive ? 'white' : 'var(--pw-text)',
                    }}
                  >
                    {category.name}
                  </span>
                  {hasChildren && (
                    <ChevronRightIcon
                      style={{
                        width: 16,
                        height: 16,
                        color: isActive ? 'rgba(255,255,255,0.8)' : 'var(--pw-text-muted)',
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s ease',
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Link>

                {/* Subcategories */}
                <AnimatePresence>
                  {hasChildren && isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      style={{ overflow: 'hidden' }}
                    >
                      <div
                        style={{
                          marginLeft: 20,
                          paddingLeft: 16,
                          borderLeft: '2px solid var(--pw-border)',
                          marginBottom: 6,
                          marginTop: 2,
                        }}
                      >
                        {category.childrenList?.map((child) => {
                          const isChildActive = currentCategory === child.slug;
                          return (
                            <Link
                              key={child.id}
                              to={`/category/${child.slug}`}
                              className="d-flex align-items-center gap-2 text-decoration-none"
                              style={{
                                padding: '8px 12px',
                                borderRadius: 10,
                                background: isChildActive
                                  ? 'rgba(232, 93, 76, 0.1)'
                                  : 'transparent',
                                color: isChildActive
                                  ? 'var(--pw-primary-strong)'
                                  : 'var(--pw-text-soft)',
                                fontSize: 13,
                                fontWeight: 500,
                                transition: 'all 0.15s ease',
                                marginBottom: 2,
                              }}
                              onMouseEnter={(e) => {
                                if (!isChildActive) {
                                  e.currentTarget.style.background = 'var(--pw-surface-tint)';
                                  e.currentTarget.style.color = 'var(--pw-text)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isChildActive) {
                                  e.currentTarget.style.background = 'transparent';
                                  e.currentTarget.style.color = 'var(--pw-text-soft)';
                                }
                              }}
                            >
                              <span
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  background: isChildActive
                                    ? 'var(--pw-primary)'
                                    : 'var(--pw-border)',
                                  flexShrink: 0,
                                }}
                              />
                              <span className="text-truncate">{child.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>
      </div>

    </div>
  );
};

export default CategorySidebar;
